import { IconCircle, IconCircleCheck, IconClock } from '@tabler/icons-react';
import { TimePicker } from '@mantine/dates';
import { useEffect, useState } from 'react';
import { Button, Loader, Popover } from '@mantine/core';
import { formatTime } from '@/lib/format-utils';
import { useTranslation } from 'react-i18next';

interface Props {
    label?: string;
    value: string | null;
    onChange: (time: string | null) => void;
    className?: string;
    saving?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    icon?: boolean;
    disabled?: boolean;
    loading?: boolean;
}

export default function SelectTimePopover({
    label,
    value,
    onChange,
    className,
    saving = false,
    size = 'sm',
    icon = false,
    disabled = false,
    loading = false,
}: Props) {
    const { t } = useTranslation();
    const [currentTime, setCurrentTime] = useState<string | null>(value);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setCurrentTime(value);
    }, [value]);

    const handleOnChange = (time: string): string => {
        const timeString = time?.substring(0, 5) || '';
        onChange(timeString);
        setOpen(false);
        return timeString;
    }

    return (
        <Popover
            withArrow
            shadow="xl"
            radius="md"
            onChange={setOpen}
            opened={open}
            closeOnClickOutside
            closeOnEscape
            clickOutsideEvents={['mousedown', 'touchstart']}
            trapFocus
        >
            <Popover.Target>
                <div
                    className={`
                        ${className} 
                        ${value ? 'font-bold' : 'font-light'}
                        text-${size}
                        w-fit
                        flex items-center gap-1
                        whitespace-nowrap
                        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    onClick={() => !disabled && setOpen(!open)}
                >
                    <div className="flex items-center gap-0.5 whitespace-nowrap">
                        {
                            loading
                                ? <Loader size={12} color="black" />
                                : <IconClock size={12} className="text-gray-500" stroke={1} />
                        }
                        {formatTime(value) || '-- : --'}
                    </div>
                </div>
            </Popover.Target>
            <Popover.Dropdown>
                <div className="space-y-4">
                    <div className="text-xs text-gray-500">{label}</div>
                    <TimePicker
                        autoFocus
                        size="md"
                        withDropdown={false}
                        withSeconds={false}
                        value={currentTime}
                        onChange={(value) => {
                            // Ensure we get a string value
                            const timeString = value || '';
                            setCurrentTime(timeString);
                        }}
                        className="w-fit mx-auto font-semibold"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && currentTime) {
                                handleOnChange(currentTime);
                            }
                        }}
                    />
                    <div className="flex justify-between gap-4">
                        <Button
                            variant="subtle"
                            size="xs"
                            color="black"
                            radius="xl"
                            onClick={() => {
                                handleOnChange(null);
                            }}
                        >
                            {t('erase')}
                        </Button>
                        <Button
                            onClick={() => {
                                handleOnChange(currentTime);
                            }}
                            size="xs"
                            color="black"
                            radius="xl"
                            loading={saving}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
